import onetime from "@-/com.npmjs.onetime";
import delay from "@-/com.npmjs.delay";
import { nativeProcessStats } from "@-/dev.improve.haniverse.modules.process-stats";
import {
  getDiagnosticsRoute,
  isDiagnosticsRecording,
  recordDiagnosticsEvent,
} from "#diagnostics-store";
import { readJsHeapStats } from "#js-runtime-stats";

export function canCollectDiagnosticsGarbage(): boolean {
  return typeof nativeProcessStats?.collectGarbage === "function";
}

const COLLECTION_DELAY_MS = 30_000;

/** The operation, rather than its initiating screen, owns the timer and duplicate guard. */
export function createDiagnosticsGarbageCollector(collectGarbage: (() => void) | undefined) {
  let pending: Promise<void> | undefined;

  return function collect(): Promise<void> {
    if (pending !== undefined) {
      return pending;
    }
    pending = (async () => {
      if (collectGarbage === undefined) {
        throw new Error("Garbage collection diagnostics are unavailable in this build.");
      }
      if (!isDiagnosticsRecording()) {
        throw new Error("Turn on diagnostic recording before collecting garbage.");
      }
      recordDiagnosticsEvent({
        kind: "marker",
        label: `Explicit diagnostic GC scheduled: ${JSON.stringify({
          delayMs: COLLECTION_DELAY_MS,
          jsHeap: readJsHeapStats(),
          route: getDiagnosticsRoute(),
        })}`,
        timestamp: Date.now(),
      });
      await delay(COLLECTION_DELAY_MS);
      if (!isDiagnosticsRecording()) {
        throw new Error("Diagnostic recording stopped before garbage was collected.");
      }
      recordDiagnosticsEvent({
        kind: "marker",
        label: `Explicit diagnostic GC started: ${JSON.stringify({
          jsHeap: readJsHeapStats(),
          route: getDiagnosticsRoute(),
        })}`,
        timestamp: Date.now(),
      });
      const startedAt = performance.now();
      try {
        collectGarbage();
      } catch (error) {
        recordDiagnosticsEvent({
          kind: "marker",
          label: `Explicit diagnostic GC failed: ${JSON.stringify({
            message: error instanceof Error ? error.message : String(error),
            route: getDiagnosticsRoute(),
          })}`,
          timestamp: Date.now(),
        });
        throw error;
      }
      recordDiagnosticsEvent({
        kind: "marker",
        label: `Explicit diagnostic GC completed: ${JSON.stringify({
          durationMs: performance.now() - startedAt,
          jsHeap: readJsHeapStats(),
          route: getDiagnosticsRoute(),
        })}`,
        timestamp: Date.now(),
      });
    })().finally(() => {
      pending = undefined;
    });
    return pending;
  };
}

const getCollector = onetime(() =>
  createDiagnosticsGarbageCollector(nativeProcessStats?.collectGarbage?.bind(nativeProcessStats)),
);

/** Explicit delayed diagnostic only. Never called by sampling, navigation, or app lifecycle. */
export function collectDiagnosticsGarbage(): Promise<void> {
  return getCollector()();
}
